import { HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Observable } from 'rxjs';
import {
  accountId, checkAvailabilityOfShopUidAPI, emptyContentType, fetchShopProfileImageAPI, getShopInfoAPI,
  shopPk, shopUid, updateShopInfoAPI, uploadShopProfileImageAPI, validateShopNameAPI
} from '../../shared/constants/end-point-constants';
import { jwtTokenKey, shopInfoKey } from '../../shared/constants/storage-key-constants';
import { JwtToken } from '../../shared/models/jwt-token.model';
import { Shop } from '../../shared/models/shop.model';
import { EditShop } from '../../shop-landing-page/shared/models/edit-shop.model';
import { ShopInfo } from '../../shop-landing-page/shared/models/shop-info.model';
import { HttpHandlerService } from '../http-handler/http-handler.service';
import { LocalStorageService } from '../local-storage/local-storage.service';
import { SessionStorageService } from '../session-storage/session-storage.service';
import { ApiConfigService } from './../api-config/api-config.service';

@Injectable({
  providedIn: 'root'
})
export class ShopInfoService {

  private currentShopInfo: ShopInfo;
  private validateShopInfo: ShopInfo;
  constructor(
    private httpHandlerService: HttpHandlerService,
    private apiConfigService: ApiConfigService,
    private sessionStorage: SessionStorageService,
    private route: Router,
    private localStorage: LocalStorageService
  ) { }
  /**
   *
   *
   * @name setShopInfoInSession
   * @description this function is used to set shop/user related info in session
   * @param info
   * @memberof ShopInfoService
   */
  setShopInfoInSession(info: ShopInfo) {
    this.sessionStorage.setItem(shopInfoKey,
      <Shop>{
        shopPk: info.shopPk || '',
        shopUid: info.shopUid || '',
        shopName: info.shopName || '',
        accountId: info.user?.uid || '',
        userId: info.user?.uid || ''
      });
  }

  /**
   *
   *
   * @description to update the shop uid in jwt token
   * @param info
   * @memberof ShopInfoService
   */
  updateJwtToken(info: ShopInfo) {
    // const jwtToken: JwtToken = this.localStorage.getItem(jwtTokenKey);
    // if (jwtToken && jwtToken.shopUid) {
    //   jwtToken.shopUid = info.shopUid;
    //   this.localStorage.setItem(jwtTokenKey, jwtToken);
    // }
  }

  /**
   *
   *
   * @name getShopInfoFromSession
   * @description this function is used to get the logged in shopinfo object
   * @returns
   * @memberof ShopInfoService
   */
  getShopInfoFromSession(): Shop {
    return this.sessionStorage.getItem(shopInfoKey) || <Shop>{};
  }

  /**
   * @name setCurrentShopInfo
   * @description this method sets the currentshopinfo in service variable
   * @param shopInfo
   * @memberof ShopInfoService
   */
  setCurrentShopInfo(shopInfo: ShopInfo) {
    this.currentShopInfo = shopInfo;
  }

  /**
   * @name setCurrentShopInfo
   * @description this method sets the validateshopinfo in service variable
   * @param shopInfo
   * @memberof ShopInfoService
   */
  setValidateShopInfo(shopInfo: ShopInfo) {
    this.validateShopInfo = shopInfo;
  }

  /**
   * @name getShopInfo
   * @description this method is used to get shop info
   * @param shopUid shop unique id
   * @returns Returns an observable
   * @memberof ShopInfoService
   */
  getShopInfo(shopid: string): Observable<ShopInfo> {
    const shopInfoObservable: Observable<ShopInfo> = new Observable(observer => {
      if (shopid) {
        const api = this.apiConfigService.getAPIPath(getShopInfoAPI);
        const urlparams = new HttpParams().append(shopUid, shopid).append('fields', 'FULL');

        if (this.currentShopInfo && this.currentShopInfo.shopUid === shopid) {
          if (!this.isShopInfoPresentInSession(shopid)) {
            this.setShopInfoInSession(this.currentShopInfo);
          }
          observer.next(this.currentShopInfo);
          observer.complete();
        } else {
          this.httpHandlerService.callServerGET(api, urlparams)
            .subscribe(response => {
              const shopInfo = new ShopInfo(response);
              this.setCurrentShopInfo(shopInfo);
              this.setShopInfoInSession(shopInfo);
              observer.next(shopInfo);
              observer.complete();
            }, error => {
              observer.error(error);
              observer.complete();
            });
        }
      } else {
        observer.error();
        observer.complete();
        // TODO: Logging Required
      }
    });
    return shopInfoObservable;
  }

  /**
   *
   *
   * @name updateShopInfo
   * @description This method is used to call update shop details API
   * @param editedInfo
   * @memberof ShopInfoService
   */
  updateShopInfo(editedInfo: EditShop) {
    const shopInfoObservable: Observable<ShopInfo> = new Observable(observer => {

      const shop: Shop = this.getShopInfoFromSession();
      const token: JwtToken = this.localStorage.getItem(jwtTokenKey);
      if (shop && shop.shopPk && token && token.aboid) {
        const api = this.apiConfigService.getAPIPath(updateShopInfoAPI);
        api.path = api.path.replace(accountId, token.aboid);
        api.path = api.path.replace(shopPk, shop.shopPk);

        this.httpHandlerService.callServerPUT(api, null, editedInfo)
          .subscribe(response => {
            this.checkIfReloadDataRequired(editedInfo);
            const shopInfo = new ShopInfo(response);
            this.setCurrentShopInfo(shopInfo);
            this.setShopInfoInSession(shopInfo);
            observer.next(shopInfo);
            observer.complete();
          }, error => {
            observer.error(error);
            observer.complete();
          });
      } else {
        observer.error();
        observer.complete();
        // TODO: Logging Required
      }
    });
    return shopInfoObservable;
  }

  /**
   *
   *
   * @name checkIfReloadDataRequired
   * @description this method is used to check if shopUid is updated and reload data with new shopUid
   * @param editedInfo
   * @memberof ShopInfoService
   */
  checkIfReloadDataRequired(editedInfo: EditShop) {
    const shop: Shop = this.getShopInfoFromSession();
    if (shop && shop.shopUid !== editedInfo.shopUID) {
      this.route.navigateByUrl(`/${editedInfo.shopUID}`);
    }
  }

  /**
   *
   *
   * @name validateShopName
   * @description This method is used to validate shop name uniqueness
   * @param newName
   * @memberof ShopInfoService
   */
  validateShopName(newName: string) {
    const validateShopNameObservable: Observable<ShopInfo> = new Observable(observer => {
      const token: JwtToken = this.localStorage.getItem(jwtTokenKey);
      if (token && token.aboid && newName) {
        const api = this.apiConfigService.getAPIPath(validateShopNameAPI);
        api.path = api.path.replace(accountId, token.aboid);
        const urlparams = new HttpParams().append(shopUid, newName);
        this.httpHandlerService.callServerPOST(api, urlparams)
          .subscribe(response => {
            observer.next(response);
            observer.complete();
          }, error => {
            observer.error(error);
            observer.complete();
          });
      } else {
        observer.error();
        observer.complete();
        // TODO: Logging Required
      }
    });
    return validateShopNameObservable;
  }

  /**
   * @name validateShopStatus
   * @description this method is used to validate shop status by fetching basic shop details
   * @param shopid
   * @returns observable string
   * @memberof ShopInfoService
   */
  validateShopStatus(shopid: string): Observable<ShopInfo> {
    const shopStatus$: Observable<ShopInfo> = new Observable(observer => {
      if (shopid) {
        const api = this.apiConfigService.getAPIPath(getShopInfoAPI);
        let urlparams = new HttpParams();
        urlparams = urlparams.append(shopUid, shopid);
        urlparams = urlparams.append('fields', 'FULL');

        if (this.validateShopInfo && this.validateShopInfo.status && this.validateShopInfo.shopUid === shopid) {
          if (!this.isShopInfoPresentInSession(shopid)) {
            this.setShopInfoInSession(this.validateShopInfo);
          }
          observer.next(this.validateShopInfo);
          observer.complete();
        } else {
          this.httpHandlerService.callServerGET(api, urlparams)
            .subscribe(response => {
              const shopInfo = new ShopInfo(response);
              this.setValidateShopInfo(shopInfo);
              this.setShopInfoInSession(shopInfo);
              observer.next(shopInfo);
              observer.complete();
            }, error => {
              observer.error(error);
              observer.complete();
            });
        }
      } else {
        observer.error();
        observer.complete();
      }
    });
    return shopStatus$;
  }

  /**
   *
   *
   * @description to check if shop info is present in session
   * @returns
   * @memberof ShopInfoService
   */
  isShopInfoPresentInSession(shopId: string): boolean {
    const shopInfo = this.getShopInfoFromSession();
    return (shopInfo && shopInfo.shopUid && shopInfo.shopUid === shopId) ? true : false;
  }

  /**
   *
   *
   * @description to check if the shop name is available or not
   * @param shopId
   * @returns
   * @memberof ShopInfoService
   */
  checkAvailabilityOfShopUid(shopId: string): Observable<boolean> {
    const shopUidAvailabilityObservable$: Observable<boolean> = new Observable(observer => {
      if (shopId) {
        const api = this.apiConfigService.getAPIPath(checkAvailabilityOfShopUidAPI);
        let urlparams = new HttpParams();
        urlparams = urlparams.append('site', 'amwayThailand');
        urlparams = urlparams.append(shopUid, shopId);
        this.httpHandlerService.callServerGET(api, urlparams)
          .subscribe(response => {
            observer.next(response);
            observer.complete();
          }, error => {
            observer.error(error);
            observer.complete();
          });
      } else {
        observer.error();
        observer.complete();
      }
    });
    return shopUidAvailabilityObservable$;
  }

  fetchShopProfileImage(shopId: string) {
    const shopProfileImage$: Observable<any> = new Observable(observer => {
      if (shopId) {
        const api = this.apiConfigService.getAPIPath(fetchShopProfileImageAPI);
        let urlparams = new HttpParams();
        urlparams = urlparams.append('site', 'amwayThailand');
        urlparams = urlparams.append(shopUid, shopId);
        urlparams = urlparams.append('fileds', 'FULL');
        this.httpHandlerService.callServerGET(api, urlparams)
          .subscribe(response => {
            observer.next(response);
            observer.complete();
          }, error => {
            observer.error(error);
            observer.complete();
          });
      } else {
        observer.error();
        observer.complete();
      }
    });
    return shopProfileImage$;
  }

  uploadShopProfileImage(shopId: string, file: any) {
    const shopProfileImage$: Observable<any> = new Observable(observer => {
      if (file) {
        const api = this.apiConfigService.getAPIPath(uploadShopProfileImageAPI);
        const form = this.createFormFromFile(file);
        const bodyParams = form;
        const contentType = emptyContentType;
        let urlparams = new HttpParams();
        urlparams = urlparams.append(shopUid, shopId);
        this.httpHandlerService.callServerPOST(api, urlparams, bodyParams, contentType)
          .subscribe(response => {
            observer.next(response);
            observer.complete();
          }, error => {
            observer.error(error);
            observer.complete();
          });
      } else {
        observer.error();
        observer.complete();
      }
    });
    return shopProfileImage$;
  }

  createFormFromFile(file) {
    const form = new FormData();
    form.append('file', file);
    return form;
  }

}
